import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { KpiTableSkeleton } from '../../feedback/KpiTableSkeleton/index.js';
import { formatPt } from '../../../lib/format.js';
import type { KpiSummary, KpiTeam } from '../../../types/api.js';

interface KpiSummaryTableProps {
  /** 表示対象行（SRE 除外・チームフィルタ適用済み） */
  rows: KpiSummary[] | undefined;
  /** 集計基準日。無い場合はカードの説明文を出さない */
  summaryDate: string | undefined;
  /** サマリー取得中 */
  isLoading: boolean;
  /** サマリー取得が失敗しているか（失敗時は「データなし」カードを出さずエラー表示に任せる） */
  hasError: boolean;
  /** チーム表示名の解決 */
  getTeamLabel: (team: KpiTeam) => string;
  /** 行クリック（そのチームのバグ明細ドリルダウンを開く） */
  onRowClick: (team: KpiTeam) => void;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business KPIページ最下部の「KPI一覧」テーブル。チーム別のバグ発生率・レベル2以上バグ率・リリースpt(90日)・バグ件数(90日)・i-point(90日)・開発pt(MTD)・リリース数(MTD)を表示し、行クリック / Enter / Space でそのチームのバグ明細モーダルを開く。モバイルはカード、デスクトップはテーブルで描画する
 * @graph-connects Card [calls] カードコンポーネント描画
 * @graph-connects CardHeader [calls] カードヘッダー描画
 * @graph-connects CardContent [calls] カードコンテンツ描画
 * @graph-connects Badge [calls] バッジコンポーネント描画
 * @graph-connects KpiTableSkeleton [calls] テーブルスケルトン描画
 * @graph-connects formatPt [calls] pt 表示フォーマット
 */
export function KpiSummaryTable({
  rows,
  summaryDate,
  isLoading,
  hasError,
  getTeamLabel,
  onRowClick,
}: KpiSummaryTableProps) {
  const { t } = useTranslation();

  return isLoading ? (
    <KpiTableSkeleton />
  ) : rows && rows.length > 0 ? (
    <Card className="animate-content-reveal">
      <CardHeader
        title={t('kpi.list')}
        description={summaryDate ? t('kpi.listDescription', { date: summaryDate }) : undefined}
      />
      <CardContent>
        {/* Mobile: Card view */}
        <div className="block md:hidden space-y-3">
          {rows.map((row) => (
            <div
              key={row.team}
              role="button"
              tabIndex={0}
              aria-label={t('kpi.listRowDetailAria', {
                team: getTeamLabel(row.team),
              })}
              className="p-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] cursor-pointer hover:bg-[var(--color-bg-hover)] transition-colors"
              onClick={() => {
                onRowClick(row.team);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onRowClick(row.team);
                }
              }}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="font-medium text-[var(--color-text-primary)]">
                  {getTeamLabel(row.team)}
                </span>
                <Badge variant={row.bugRate !== null && row.bugRate < 3 ? 'success' : 'warning'}>
                  {row.bugRate !== null ? `${(row.bugRate * 100).toFixed(1)}%` : '-'}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-[var(--color-text-muted)]">{t('kpi.bugRate')}</p>
                  <p className="text-[var(--color-text-primary)] font-medium">
                    {row.bugRate !== null ? `${(row.bugRate * 100).toFixed(1)}%` : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-[var(--color-text-muted)]">{t('kpi.bugRateLevel2Plus')}</p>
                  <p className="text-[var(--color-text-primary)] font-medium">
                    {row.bugRateLevel2Plus != null
                      ? `${(row.bugRateLevel2Plus * 100).toFixed(1)}%`
                      : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-[var(--color-text-muted)]">{t('kpi.releasePoint90d')}</p>
                  <p className="text-[var(--color-text-primary)] font-medium">
                    {formatPt(row.releasePoint90d ?? 0)}
                  </p>
                </div>
                <div>
                  <p className="text-[var(--color-text-muted)]">{t('kpi.bugCount90d')}</p>
                  <p className="text-[var(--color-text-primary)] font-medium">
                    {row.bugCount90d ?? 0}
                  </p>
                </div>
                <div>
                  <p className="text-[var(--color-text-muted)]">{t('kpi.ipoint90d')}</p>
                  <p className="text-[var(--color-text-primary)] font-medium">
                    {(row.ipoint90d ?? 0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-[var(--color-text-muted)]">{t('kpi.developPointMtd')}</p>
                  <p className="text-[var(--color-text-primary)] font-medium">
                    {formatPt(row.developPointMtd ?? 0)}
                  </p>
                </div>
                <div>
                  <p className="text-[var(--color-text-muted)]">{t('kpi.releaseCountMtd')}</p>
                  <p className="text-[var(--color-text-primary)] font-medium">
                    {row.releaseCountMtd}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop: Table view */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--color-border)]">
                <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                  {t('kpi.scope.label')}
                </th>
                <th className="text-right py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                  {t('kpi.bugRate')}
                </th>
                <th className="text-right py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                  {t('kpi.bugRateLevel2Plus')}
                </th>
                <th className="text-right py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                  {t('kpi.releasePoint90d')}
                </th>
                <th className="text-right py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                  {t('kpi.bugCount90d')}
                </th>
                <th className="text-right py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                  {t('kpi.ipoint90d')}
                </th>
                <th className="text-right py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                  {t('kpi.developPointMtd')}
                </th>
                <th className="text-right py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                  {t('kpi.releaseCountMtd')}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.team}
                  role="button"
                  tabIndex={0}
                  aria-label={t('kpi.listRowDetailAria', {
                    team: getTeamLabel(row.team),
                  })}
                  className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)] transition-colors cursor-pointer"
                  onClick={() => {
                    onRowClick(row.team);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onRowClick(row.team);
                    }
                  }}
                >
                  <td className="py-3 px-4 text-sm text-[var(--color-text-primary)] font-medium">
                    {getTeamLabel(row.team)}
                  </td>
                  <td className="py-3 px-4 text-sm text-[var(--color-text-primary)] text-right">
                    {row.bugRate !== null ? (
                      <Badge variant={row.bugRate < 3 ? 'success' : 'warning'} size="sm">
                        {`${(row.bugRate * 100).toFixed(1)}%`}
                      </Badge>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="py-3 px-4 text-sm text-[var(--color-text-primary)] text-right">
                    {row.bugRateLevel2Plus != null ? (
                      <Badge variant={row.bugRateLevel2Plus < 2 ? 'success' : 'warning'} size="sm">
                        {`${(row.bugRateLevel2Plus * 100).toFixed(1)}%`}
                      </Badge>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="py-3 px-4 text-sm text-[var(--color-text-primary)] text-right">
                    {formatPt(row.releasePoint90d ?? 0)}
                  </td>
                  <td className="py-3 px-4 text-sm text-[var(--color-text-primary)] text-right">
                    {row.bugCount90d ?? 0}
                  </td>
                  <td className="py-3 px-4 text-sm text-[var(--color-text-primary)] text-right">
                    {(row.ipoint90d ?? 0).toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-sm text-[var(--color-text-primary)] text-right">
                    {formatPt(row.developPointMtd ?? 0)}
                  </td>
                  <td className="py-3 px-4 text-sm text-[var(--color-text-primary)] text-right">
                    {row.releaseCountMtd ?? 0}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  ) : !hasError ? (
    <Card className="animate-content-reveal">
      <CardContent>
        <div className="empty-state">
          <p className="text-[var(--color-text-muted)]">{t('status.noData')}</p>
        </div>
      </CardContent>
    </Card>
  ) : null;
}
